/**
 * API 呼び出しの失敗（{@link ApiError}）と、そこから情報を取り出す関数。
 *
 * @remarks
 * `customFetch`（Orval の生成フック）と `fetchWithAuth`（Blob のダウンロード等）が投げる。
 * 画面のエラーダイアログ（#456）に出すため、サーバーの説明・コードに加えて、リクエスト
 * （メソッド・パス・クエリ）・エラー ID・時刻・500 のスタックを持つ。
 * `@/lib/api/mutator` からも再エクスポートしている（既存の import を壊さないため）。
 */

/** 失敗したリクエスト。 */
export interface ApiRequestInfo {
  /** HTTP メソッド（大文字）。 */
  method: string;
  /** パス＋クエリ（ベース URL は含めない）。 */
  url: string;
  /** FE が振って `X-Request-ID` で送ったエラー ID。 */
  requestId: string;
}

/** 500 の応答の `error`（認証済みの要求で、サーバーが例外の型・説明・スタックを返したとき）。 */
export interface ServerErrorTrace {
  /** 例外の型（`sqlalchemy.exc.IntegrityError` など）。 */
  type: string;
  /** 例外の説明の 1 行目。 */
  message: string;
  /** スタック（呼び出し順・アプリのフレームと例外が起きたフレーム）。 */
  stack: string[];
}

/** {@link ApiError} の補足情報（コンストラクタの第 6 引数）。 */
export interface ApiErrorInfo {
  /** 失敗したリクエスト。 */
  request?: ApiRequestInfo | null;
  /** サーバーが返したエラー ID（応答の `request_id`・ヘッダ `X-Request-ID`）。 */
  requestId?: string | null;
  /** サーバーがエラーを記録した時刻（応答の `timestamp`）。 */
  serverTimestamp?: string | null;
  /** 500 の応答の `error`。 */
  serverError?: ServerErrorTrace | null;
  /** 本文が JSON でなかったときの先頭（ALB のエラーページなど）。 */
  bodyExcerpt?: string | null;
  /** 失敗した時刻（既定は作った時刻）。 */
  occurredAt?: Date;
  /** 通信エラーの元の例外（`TypeError: Failed to fetch` など）。 */
  cause?: unknown;
}

/**
 * API 呼び出しの失敗（非 2xx・通信エラー）。サーバーが返した説明文とリクエストの情報を保持する。
 *
 * @remarks
 * `message` は従来どおり `API request failed: 409 Conflict` 形式を保つ
 * （`statusFromError` 等の既存の文字列判定を壊さないため）。UI 向けの説明は
 * `serverMessage` を使う（`describeApiError`）。通信エラー（サーバーに届かない・応答が
 * 無い）は `status` が 0。
 */
export class ApiError extends Error {
  /** HTTP ステータスコード（通信エラーは 0）。 */
  readonly status: number;
  /** HTTP ステータスの説明（`Conflict` など。通信エラーは `Network Error`）。 */
  readonly statusText: string;
  /** サーバーが返した日本語の説明文（取り出せなければ null）。 */
  readonly serverMessage: string | null;
  /**
   * サーバーの機械可読エラーコード（`{code, message}` 形式のとき。取り出せなければ null）。
   * 例: 403 の `EMPLOYEE_NOT_LINKED` / `USER_DISABLED`（AccountGate が分岐に使う）。
   */
  readonly code: string | null;
  /**
   * サーバーの構造化情報（`{code, message, details}` 形式のとき。無ければ null）。
   * 例: 409 `UNCOVERED_ASSIGNEES` の `{uncovered: [{evidence_item_id, evidence_name, employee_id}]}`。
   */
  readonly details: unknown;
  /** 失敗したリクエスト（テスト等で作ったものは null）。 */
  readonly request: ApiRequestInfo | null;
  /** エラー ID（サーバーが返した値。無ければ FE が振った値）。ログの突合キー。 */
  readonly requestId: string | null;
  /** サーバーがエラーを記録した時刻。 */
  readonly serverTimestamp: string | null;
  /** 500 の応答の例外の型・説明・スタック。 */
  readonly serverError: ServerErrorTrace | null;
  /** 本文が JSON でなかったときの先頭。 */
  readonly bodyExcerpt: string | null;
  /** 失敗した時刻。 */
  readonly occurredAt: Date;
  /** 画面のエラーダイアログに積んだか（同じ失敗を二重に積まない）。 */
  reported = false;

  constructor(
    status: number,
    statusText: string,
    serverMessage: string | null,
    code: string | null = null,
    details: unknown = null,
    info: ApiErrorInfo = {},
  ) {
    super(
      `API request failed: ${status} ${statusText}`,
      info.cause !== undefined ? { cause: info.cause } : undefined,
    );
    this.name = "ApiError";
    this.status = status;
    this.statusText = statusText;
    this.serverMessage = serverMessage;
    this.code = code;
    this.details = details;
    this.request = info.request ?? null;
    this.requestId = info.requestId ?? info.request?.requestId ?? null;
    this.serverTimestamp = info.serverTimestamp ?? null;
    this.serverError = info.serverError ?? null;
    this.bodyExcerpt = info.bodyExcerpt ?? null;
    this.occurredAt = info.occurredAt ?? new Date();
  }
}

/**
 * catch した例外からサーバーの構造化情報を取り出す（{@link ApiError} 以外・無ければ null）。
 *
 * @param error - catch した例外。
 */
export function errorDetailsOf(error: unknown): unknown {
  return error instanceof ApiError ? error.details : null;
}

/**
 * catch した例外からサーバーの機械可読エラーコードを取り出す（{@link ApiError} 以外は null）。
 *
 * @param error - catch した例外。
 */
export function errorCodeOf(error: unknown): string | null {
  return error instanceof ApiError ? error.code : null;
}

/**
 * catch した例外からサーバーの説明文を取り出す（{@link ApiError} 以外は null）。
 *
 * @param error - catch した例外。
 */
export function serverMessageOf(error: unknown): string | null {
  return error instanceof ApiError ? error.serverMessage : null;
}

/**
 * 取り直せば通ることがある一時的な失敗のステータス（ALB が返す 502・503・504）。
 *
 * @remarks
 * 502 の多くは、API・FE がちょうど閉じた接続に ALB が要求を送ったもの（接続を保つ時間のずれ。
 * `infra/lib/nexus-stack.ts` で API・FE の側を ALB より長くしてある）。503・504 はタスクの入れ替え中・
 * 時間切れ。500 は API の不具合で、取り直しても同じになるので含めない。
 */
export const TRANSIENT_STATUSES: ReadonlySet<number> = new Set([502, 503, 504]);

/**
 * 取り直せば通ることがある一時的な失敗か（通信エラー・{@link TRANSIENT_STATUSES}）。
 *
 * @param error - catch した例外。
 */
export function isTransientApiError(error: unknown): boolean {
  return (
    error instanceof ApiError && (error.status === 0 || TRANSIENT_STATUSES.has(error.status))
  );
}

/** 本文が JSON でないときに残す先頭の文字数。 */
const BODY_EXCERPT_LENGTH = 500;

const stringField = (body: object, key: string): string | null => {
  const value = (body as Record<string, unknown>)[key];
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
};

/**
 * エラー応答の本文（文字列）から説明・コード・補足・エラー ID・時刻・500 のスタックを取り出す。
 *
 * `{code, message, details, request_id, timestamp, error}`（app/core/errors.py）と
 * `{detail: string}`（旧形式の HTTPException）に対応する。422 の `detail` は
 * オブジェクト配列なので説明には採用しない（`message` に日本語の要約が入る）。JSON でなければ
 * 先頭を `bodyExcerpt` に残す（ALB のエラーページなど）。
 *
 * @param text - 応答の本文。
 */
export function parseErrorBody(text: string): {
  serverMessage: string | null;
  code: string | null;
  details: unknown;
  requestId: string | null;
  serverTimestamp: string | null;
  serverError: ServerErrorTrace | null;
  bodyExcerpt: string | null;
} {
  const empty = {
    serverMessage: null,
    code: null,
    details: null,
    requestId: null,
    serverTimestamp: null,
    serverError: null,
    bodyExcerpt: null,
  };
  if (text.trim() === "") return empty;
  let body: unknown;
  try {
    body = JSON.parse(text);
  } catch {
    return { ...empty, bodyExcerpt: text.slice(0, BODY_EXCERPT_LENGTH) };
  }
  if (typeof body !== "object" || body === null) return empty;
  const record = body as Record<string, unknown>;
  const trace = record.error;
  let serverError: ServerErrorTrace | null = null;
  if (typeof trace === "object" && trace !== null) {
    const t = trace as Record<string, unknown>;
    serverError = {
      type: typeof t.type === "string" ? t.type : "",
      message: typeof t.message === "string" ? t.message : "",
      stack: Array.isArray(t.stack) ? t.stack.filter((s): s is string => typeof s === "string") : [],
    };
  }
  return {
    serverMessage: stringField(body, "message") ?? stringField(body, "detail"),
    code: stringField(body, "code"),
    details: record.details ?? null,
    requestId: stringField(body, "request_id"),
    serverTimestamp: stringField(body, "timestamp"),
    serverError,
    bodyExcerpt: null,
  };
}
